'use client'
import React, { useState } from 'react'
import dynamic from 'next/dynamic'

const Map = dynamic(() => import('@components/Map'), {
  loading: () => <p className="text-secondary">Loading map...</p>,
  ssr: false,
})

const i18n = {
  location:  { en: 'Location',            th: 'ที่ตั้ง',            cn: '位置',         ru: 'Расположение',       fr: 'Emplacement',              de: 'Lage',                    it: 'Posizione',           ja: '所在地',           ko: '위치',            es: 'Ubicación',            tr: 'Konum',            he: 'מיקום',         ar: 'الموقع'              },
  area:      { en: 'Area',                th: 'พื้นที่',             cn: '区域',         ru: 'Район',              fr: 'Zone',                     de: 'Gebiet',                  it: 'Area',                ja: 'エリア',           ko: '지역',            es: 'Área',                 tr: 'Bölge',            he: 'אזור',          ar: 'المنطقة'             },
  city:      { en: 'City',                th: 'เมือง',               cn: '城市',         ru: 'Город',              fr: 'Ville',                    de: 'Stadt',                   it: 'Città',               ja: '都市',             ko: '도시',            es: 'Ciudad',               tr: 'Şehir',            he: 'עיר',           ar: 'المدينة'             },
  showOnMap: { en: 'Show on map',         th: 'แสดงบนแผนที่',        cn: '在地图上显示',   ru: 'Показать на карте',  fr: 'Voir sur la carte',        de: 'Auf Karte zeigen',        it: 'Mostra sulla mappa',  ja: '地図で見る',       ko: '지도에서 보기',    es: 'Ver en el mapa',       tr: 'Haritada göster',  he: 'הצג במפה',      ar: 'عرض على الخريطة'     },
  hideMap:   { en: 'Hide map',            th: 'ซ่อนแผนที่',          cn: '隐藏地图',      ru: 'Скрыть карту',       fr: 'Masquer la carte',         de: 'Karte ausblenden',        it: 'Nascondi mappa',      ja: '地図を隠す',       ko: '지도 숨기기',     es: 'Ocultar mapa',         tr: 'Haritayı gizle',   he: 'הסתר מפה',      ar: 'إخفاء الخريطة'       },
  openOnMap: { en: 'Open on Google Maps', th: 'เปิดใน Google Maps',  cn: '在谷歌地图打开', ru: 'Открыть в Google Maps', fr: 'Ouvrir dans Google Maps', de: 'In Google Maps öffnen',  it: 'Apri su Google Maps', ja: 'Google マップで開く', ko: 'Google 지도에서 열기', es: 'Abrir en Google Maps', tr: "Google Maps'te aç", he: 'פתח ב-Google Maps', ar: 'فتح في خرائط Google' },
}

// Google Maps locale codes (BCP-47 hl param)
const GOOGLE_HL = {
  en: 'en', th: 'th', cn: 'zh-TW', ru: 'ru', fr: 'fr', de: 'de',
  it: 'it', ja: 'ja', ko: 'ko',    es: 'es', tr: 'tr', he: 'iw', ar: 'ar',
}

// Mapbox locale codes
const MAPBOX_LOCALE = {
  en: 'en', th: 'th', cn: 'zh', ru: 'ru', fr: 'fr', de: 'de',
  it: 'it', ja: 'ja', ko: 'ko', es: 'es', tr: 'tr', he: 'he', ar: 'ar',
}

const t = (key, lang) => i18n[key]?.[lang] || i18n[key]?.en

const Location = ({ districts, maskLocation, location, slug, latitude, longitude, lang = 'en', config = {} }) => {
  const {
    provider         = 'google',
    showToggleButton = true,
    mapStartOpen     = false,
    titleLabels      = {},
    titleColor       = 'var(--color-main)',
    titleFw          = '600',
    titleSize        = '1.25rem',
    titleAlign       = 'left',
    showTitle        = true,
    btnStyle         = 'outlined',
    btnColor         = 'var(--color-main)',
    btnTextColor     = '#ffffff',
    btnAlign         = 'right',
    btnFontSize      = '0.875rem',
    btnFontWeight    = '400',
    btnPaddingX      = '16',
    btnPaddingY      = '4',
    btnBorderRadius  = '6',
    showMapLabels    = {},
    hideMapLabels    = {},
    linkService      = 'google',
    linkTarget       = '_blank',
    linkLabels       = {},
    showDistricts    = true,
    showCity         = true,
  } = config

  const isRTL = lang === 'he' || lang === 'ar'
  const [open, setOpen] = useState(!showToggleButton || mapStartOpen)

  const resolveLabel = (obj, fallback) => obj?.[lang]?.trim() || obj?.en?.trim() || fallback

  const resolvedTitle   = resolveLabel(titleLabels,   t('location',  lang))
  const resolvedShowMap = resolveLabel(showMapLabels, t('showOnMap', lang))
  const resolvedHideMap = resolveLabel(hideMapLabels, t('hideMap',   lang))

  const buildMapUrl = () => {
    const lat = latitude
    const lng = longitude
    const hl  = GOOGLE_HL[lang] ?? 'en'
    switch (linkService) {
      case 'osm':    return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}&zoom=15`
      case 'waze':   return `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`
      case 'apple':  return `https://maps.apple.com/?ll=${lat},${lng}&z=15`
      case 'mapbox': return `https://www.mapbox.com/maps/?lng=${lng}&lat=${lat}&zoom=15&language=${MAPBOX_LOCALE[lang] ?? 'en'}`
      default:       return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}&hl=${hl}`
    }
  }

  // For RTL flip btn alignment: left↔right
  const effectiveBtnAlign = isRTL
    ? (btnAlign === 'left' ? 'right' : btnAlign === 'right' ? 'left' : btnAlign)
    : btnAlign
  const justifyMap = { left: 'flex-start', center: 'center', right: 'flex-end' }

  const px = `${btnPaddingY}px ${btnPaddingX}px`
  const radius = btnStyle === 'pill' ? 999 : Number(btnBorderRadius)
  const btnBaseStyle = {
    cursor: 'pointer', marginTop: 8, fontSize: btnFontSize,
    fontWeight: btnFontWeight, background: 'none', border: 'none', padding: 0, lineHeight: 1.4,
  }

  const btnEl = (label, onClick) => {
    if (btnStyle === 'filled') return <button onClick={onClick} style={{ ...btnBaseStyle, background: btnColor, color: btnTextColor, padding: px, borderRadius: radius }}>{label}</button>
    if (btnStyle === 'pill')   return <button onClick={onClick} style={{ ...btnBaseStyle, color: btnColor, border: `1px solid ${btnColor}`, padding: px, borderRadius: 999 }}>{label}</button>
    if (btnStyle === 'text')   return <button onClick={onClick} style={{ ...btnBaseStyle, color: btnColor, textDecoration: 'underline', textUnderlineOffset: 2 }}>{label}</button>
    return <button onClick={onClick} style={{ ...btnBaseStyle, color: btnColor, border: `1px solid ${btnColor}`, padding: px, borderRadius: radius }}>{label}</button>
  }

  return (
    <div className="mb-3 pb-3" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      {showTitle && (
        <h2 style={{ color: titleColor, fontWeight: titleFw, fontSize: titleSize, textAlign: isRTL ? 'right' : titleAlign, margin: 0, marginBottom: 6 }}>
          {resolvedTitle}
        </h2>
      )}

      <div className="flex flex-col">
        {showDistricts && districts?.[0] && (
          <span>{t('area', lang)}: {districts[0].subdistrict}, {districts[0].district}</span>
        )}
        {showCity && (
          <span>{t('city', lang)}: {location}</span>
        )}

        {provider === 'link' ? (
          <div style={{ display: 'flex', justifyContent: justifyMap[effectiveBtnAlign] ?? 'flex-end', marginTop: 8 }}>
            <a href={buildMapUrl()} target={linkTarget} rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              {btnEl(resolveLabel(linkLabels, t('openOnMap', lang)), () => {})}
            </a>
          </div>
        ) : (
          <>
            {showToggleButton && (
              <div style={{ display: 'flex', justifyContent: justifyMap[effectiveBtnAlign] ?? 'flex-end' }}>
                {btnEl(open ? resolvedHideMap : resolvedShowMap, () => setOpen(o => !o))}
              </div>
            )}
            {open && (
              <Map
                latitude={latitude}
                longitude={longitude}
                maskLocation={maskLocation}
                lang={lang}
                config={config}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default Location